package kz.dentalcrm.app.screens.patients

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kz.dentalcrm.app.screens.patients.mvi.PatientsViewEvent
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.Plus
import compose.icons.feathericons.Search
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.patients
import dentalcrm.composeapp.generated.resources.search
import kotlinx.collections.immutable.PersistentList
import kz.dentalcrm.app.core.ui.components.UISelfTextInput
import kz.dentalcrm.app.core.ui.components.lazy_column.PaginatedLazyColumn
import kz.dentalcrm.app.core.ui.components.refresh.PullToRefresh
import kz.dentalcrm.app.core.ui.components.refresh.rememberPullToRefreshState
import kz.dentalcrm.app.data.network.response_model.PatientData
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.navigation.NavigationResults
import kz.dentalcrm.app.navigation.PatientDetailRoute
import kz.dentalcrm.app.navigation.resultFlow
import kz.dentalcrm.app.screens.patients.add.AddPatientDialog
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.theme.DefaultTheme
import kz.dentalcrm.app.theme.shimmer.ShimmerBounds
import kz.dentalcrm.app.theme.shimmer.rememberShimmer
import kz.dentalcrm.app.theme.shimmer.shimmer
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun PatientsScreen() {
    val navigator: AppNavigator = koinInject()
    val results: NavigationResults = koinInject()
    val viewModel: PatientViewModel = koinViewModel()

    LaunchedEffect(results) {
        results.resultFlow<PatientDetailRoute.Result>(PatientDetailRoute.RESULT_KEY)
            .collect { result ->
                when {
                    result.deleted -> viewModel.setEvent(PatientsViewEvent.RemovePatient(result.patientId))
                    result.updated -> viewModel.setEvent(PatientsViewEvent.Refresh)
                }
                results.clear(PatientDetailRoute.RESULT_KEY)
            }
    }

    val state by viewModel.viewState.collectAsState()

    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        var showAddPatient by remember { mutableStateOf(false) }

        if (showAddPatient) {
            AddPatientDialog(
                onAdd = {
                    viewModel.setEvent(PatientsViewEvent.Refresh)
                },
                onDismiss = {
                    showAddPatient = false
                }
            )
        }

        PatientsContent(
            isLoading = state.isLoading,
            patients = state.patients,
            patientClicked = {
                navigator.navigate(PatientDetailRoute(patientId = it.id))
            },
            isError = state.error.takeIf { it.isNullOrEmpty().not() } ?: "",
            add = {
                showAddPatient = true
            },
            refresh = {
                viewModel.setEvent(PatientsViewEvent.Refresh)
            },
            loadNextPage = {
                viewModel.setEvent(PatientsViewEvent.LoadNextPage)
            },
            searchQuery = state.searchQuery,
            onSearchQueryChanged = { viewModel.setEvent(PatientsViewEvent.OnSearchQueryChanged(it)) }
        )
    }
}

@Composable
fun PatientsContent(
    isLoading: Boolean,
    isError: String,
    patients: PersistentList<PatientData>,
    patientClicked: (PatientData) -> Unit,
    loadNextPage: () -> Unit,
    add: () -> Unit,
    refresh: () -> Unit,
    searchQuery: String,
    onSearchQueryChanged: (String) -> Unit
) {
    var isRefreshing by remember { mutableStateOf(false) }
    val pullToRefreshState = rememberPullToRefreshState(isRefreshing = isRefreshing)
    LaunchedEffect(isLoading) { if (!isLoading) isRefreshing = false }

    Box(
        modifier = Modifier.fillMaxSize()
            .safeDrawingPadding()
            .padding(horizontal = 12.dp)
    ) {
        Column(
            modifier = Modifier.fillMaxSize()
        ) {
            ContentHeader(
                modifier = Modifier.fillMaxWidth(),
                text = stringResource(Res.string.patients)
            )
            UISelfTextInput(
                modifier = Modifier.fillMaxWidth(),
                value = searchQuery,
                valueChanged = onSearchQueryChanged,
                label = stringResource(Res.string.search),
                singleLine = true,
                leadingIcon = FeatherIcons.Search
            )
            PullToRefresh(
                state = pullToRefreshState,
                onRefresh = { isRefreshing = true; refresh() },
                modifier = Modifier.fillMaxSize()
            ) {
                Body(
                    patients = patients,
                    patientClicked = patientClicked,
                    loading = isLoading,
                    loadNextPage = loadNextPage,
                    isError = isError
                )
            }
        }
        FloatingActionButton(
            modifier = Modifier
                .navigationBarsPadding()
                .padding(
                    bottom = 96.dp,
                    start = 12.dp,
                    end = 12.dp,
                    top = 16.dp,
                ).align(Alignment.BottomEnd),
            shape = RoundedCornerShape(50),
            containerColor = DefaultTheme.colors.primary,
            contentColor = DefaultTheme.colors.onPrimary,
            onClick = { add() }
        ) {
            Icon(
                imageVector = FeatherIcons.Plus,
                contentDescription = null
            )
        }
    }
}

@Composable
private fun Body(
    patients: List<PatientData>,
    patientClicked: (PatientData) -> Unit = {},
    loading: Boolean = false,
    isError: String = "",
    loadNextPage: () -> Unit = {},
) {
    val shimmer = rememberShimmer(ShimmerBounds.Window)
    val listState = rememberLazyListState()

    if (loading && patients.isEmpty()) {
        Column(modifier = Modifier.widthIn().padding(top = 16.dp)) {
            repeat(8) { PatientItemSkeleton(shimmer = shimmer) }
            Spacer(modifier = Modifier.height(220.dp))
        }
    } else {
        val listModifier = if (loading) Modifier.widthIn().shimmer(shimmer) else Modifier.widthIn()
        PaginatedLazyColumn(
            modifier = listModifier,
            loadMoreItems = { loadNextPage() },
            listState = listState,
            buffer = 5,
            isLoading = loading,
            paginatedTextError = isError,
            retryOnPaginated = { loadNextPage() }
        ) {
            item {
                Spacer(modifier = Modifier.height(16.dp))
            }
            itemsIndexed(
                items = patients
            ) { _, patient ->
                PatientItem(
                    item = patient,
                    onClick = { patientClicked(patient) }
                )
            }
            item {
                Spacer(modifier = Modifier.height(220.dp))
            }
        }
    }
}

@Composable
private fun PatientItemSkeleton(shimmer: kz.dentalcrm.app.theme.shimmer.Shimmer) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = DefaultTheme.colors.primarySubdued,
                shape = RoundedCornerShape(16.dp)
            ),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Spacer(modifier = Modifier.width(12.dp))
        Box(
            modifier = Modifier
                .size(40.dp)
                .shimmer(shimmer)
                .background(
                    color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                    shape = RoundedCornerShape(40.dp)
                )
        )
        Spacer(modifier = Modifier.width(16.dp))
        Column(modifier = Modifier.weight(1f)) {
            Spacer(modifier = Modifier.height(12.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.6f)
                    .height(14.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(4.dp))
            Box(
                modifier = Modifier
                    .fillMaxWidth(0.4f)
                    .height(12.dp)
                    .shimmer(shimmer)
                    .background(
                        color = DefaultTheme.colors.onSurface.copy(alpha = 0.12f),
                        shape = RoundedCornerShape(4.dp)
                    )
            )
            Spacer(modifier = Modifier.height(12.dp))
        }
        Spacer(modifier = Modifier.width(32.dp))
    }
    Spacer(Modifier.height(12.dp))
}
